import { useMutation } from "@tanstack/react-query"
import download from "downloadjs"
import { toast } from "react-toastify"

import { parseFileName, useDownloadRequest } from "~/hooks/useDownloadRequest"
import type { ForecastExportClientInput } from "~/server/api/excelExportStream"
import { api, type RouterInputs } from "~/utils/api"
import { XLSX_CONTENT_TYPE } from "~/utils/excelSheet"

type ForecastExportInput = ForecastExportClientInput

function useForecastExport(level: "sku" | "variant", successMessage: string) {
  const postDownload = useDownloadRequest()

  return useMutation({
    mutationFn: async (input: ForecastExportInput) => {
      const variantCount = input.variantIds.length.toLocaleString()
      const exportLabel = `${level === "sku" ? "SKU " : ""}forecasts for ${variantCount} variants`
      const toastId = toast.loading(`Preparing ${exportLabel}…`)
      try {
        const response = await postDownload(
          "/download/forecast-export",
          { ...input, level },
          "Failed to export forecasts.",
        )

        const totalRows = Number(response.headers.get("x-total-rows"))
        const estimatedBytes = Number(response.headers.get("x-estimated-size"))
        const downloadLabel =
          totalRows > 0
            ? `Downloading ${totalRows.toLocaleString()} rows…`
            : `Downloading ${exportLabel}…`
        toast.update(toastId, {
          render: downloadLabel,
          ...(estimatedBytes > 0 ? { isLoading: false, progress: 0.01 } : {}),
        })

        const chunks: BlobPart[] = []
        const reader = response.body?.getReader()
        if (reader) {
          let receivedBytes = 0
          let lastShownBytes = 0
          for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            chunks.push(value)
            receivedBytes += value.length
            if (receivedBytes - lastShownBytes > 512 * 1024) {
              lastShownBytes = receivedBytes
              toast.update(toastId, {
                render: `${downloadLabel} ${(receivedBytes / 1048576).toFixed(1)} MB`,
                ...(estimatedBytes > 0
                  ? { progress: Math.min(receivedBytes / estimatedBytes, 0.99) }
                  : {}),
              })
            }
          }
        } else {
          chunks.push(await response.blob())
        }

        const contentType = response.headers.get("content-type") ?? XLSX_CONTENT_TYPE
        const fileName =
          parseFileName(response.headers.get("content-disposition")) ??
          `Forecasts-${new Date().toISOString().slice(0, 10)}.xlsx`
        download(new Blob(chunks, { type: contentType }), fileName, contentType)
        toast.update(toastId, {
          render: successMessage,
          type: "success",
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
          progress: 0,
        })
      } catch (err) {
        toast.update(toastId, {
          render: err instanceof Error ? err.message : "Failed to export forecasts.",
          type: "error",
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
          progress: 0,
        })
        throw err
      }
    },
  })
}

export const useDownloadVariantForecast = () => {
  return useForecastExport("variant", "Forecasts for variants successfully downloaded.")
}

export const useDownloadSKUForecast = () => {
  return useForecastExport("sku", "Forecasts for SKUs successfully downloaded.")
}

function downloadFile(data: {
  fileContents: Uint8Array
  fileName: string
  contentType: string
}): void {
  const blob = new Blob([data.fileContents as Uint8Array<ArrayBuffer>], { type: data.contentType })
  download(blob, data.fileName, data.contentType)
}

export const useDownloadMultiplePurchaseOrders = () => {
  return api.excelDownload.multiplePurchaseOrders.useMutation({
    onSuccess: (data) => {
      downloadFile(data)
      toast.success("Purchase order successfully downloaded.")
    },
    onError: () => {
      toast.error("Failed to download purchase orders")
    },
  })
}

export type HakiExportInput = RouterInputs["excelDownload"]["hakiExport"]

export const useDownloadHakiExport = () => {
  const utils = api.useUtils()

  return useMutation({
    mutationFn: (input: HakiExportInput) => utils.excelDownload.hakiExport.fetch(input),
    onSuccess: (data) => {
      downloadFile(data)
      toast.success("Downloaded to Excel.")
    },
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : "Failed to download.")
    },
  })
}
